/**
 * Sidebar
 *
 * App navigation with expandable sections, enterprise logo display, namespace
 * selector, and mobile handling. Includes lightweight tooltips and active
 * route highlighting.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSidebar } from '../../../context/sidebar';
import { useAuth } from '../../../context/auth';
import { FileText, X, Building, Loader2, Lightbulb, HelpCircle, ChevronDown, Video, Users, Sparkles, MessageSquareText, Globe } from 'lucide-react';
import NamespaceSelector from '../../namespaces/namespace/NamespaceSelector';
import { useNamespace } from '../../../context/namespace';
import { createPortal } from 'react-dom';
import { EnterpriseLogoSkeleton } from '../../ui/feedback/skeleton.jsx';
import api from '../../../api';

// Hook to detect dark mode
function useDarkMode() {
  const [isDark, setIsDark] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

// Improved tooltip component with CSS transitions and debouncing
function Tooltip({ text, children }) {
  const [isVisible, setIsVisible] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  
  // Show tooltip with delay to prevent flickering on accidental hover
  const showTooltip = () => {
    // Clear any existing timeout to prevent flickering
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set visibility first, then fade in
    setIsVisible(true);
    // Small delay before showing to prevent flash on quick mouse movements
    timeoutRef.current = setTimeout(() => {
      setOpacity(1);
    }, 50);
  };
  
  // Hide tooltip with transition
  const hideTooltip = () => {
    // Start fade out animation
    setOpacity(0);
    // Remove from DOM after transition completes
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200); // Match transition duration
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Calculate and set position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // On mobile, position tooltip in view
        setPosition({
          top: rect.top,
          left: 100, // Fixed position for mobile
        });
      } else {
        // On desktop, position to the right of the trigger
        setPosition({
          top: rect.top,
          left: rect.right + 10,
        });
      }
    }
  }, [isVisible]);
  
  // Split text into paragraphs for better formatting
  const textParagraphs = text.split('\n\n').filter(paragraph => paragraph.trim());
  
  // Portal element with transitions
  const tooltipElement = (
    <div 
      ref={tooltipRef}
      className="fixed z-[9999] bg-gray-800 text-white rounded-md shadow-lg transition-opacity duration-200"
      style={{ 
        top: `${position.top}px`, 
        left: `${position.left}px`,
        maxWidth: '320px',
        padding: '12px 16px',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        opacity: opacity,
        pointerEvents: 'none', // Prevents the tooltip from interfering with mouse events
      }}
    >
      <div 
        className="absolute right-full top-2" 
        style={{ 
          borderTop: '6px solid transparent',
          borderRight: '8px solid #1f2937',
          borderBottom: '6px solid transparent'
        }}
      ></div>
      {textParagraphs.map((paragraph, index) => (
        <p key={index} className={index > 0 ? 'mt-2' : ''}>
          {paragraph.trim()}
        </p>
      ))}
    </div>
  );

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {isVisible && createPortal(tooltipElement, document.body)}
    </>
  );
}

// Enterprise Logo Display Component
function EnterpriseLogoDisplay({ isOpen }) {
  const { user } = useAuth();
  const [enterpriseData, setEnterpriseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchEnterpriseData = async () => {
      console.log('EnterpriseLogoDisplay: Starting fetch for user:', {
        userId: user?.uid,
        email: user?.email,
        enterpriseId: user?.enterpriseId,
        isEnterpriseUser: user?.isEnterpriseUser,
        hasEnterpriseDataInAuth: !!user?.enterpriseData
      });

      // First, check if we already have enterprise data from AuthContext
      if (user?.enterpriseData) {
        console.log('EnterpriseLogoDisplay: Using enterprise data from AuthContext');
        setEnterpriseData(user.enterpriseData);
        setLoading(false);
        setError(false);
        return;
      }

      if (!user?.email) {
        console.log('EnterpriseLogoDisplay: No user email, skipping fetch');
        setLoading(false);
        setError(true);
        return;
      }

      try {
        setError(false);
        let enterpriseFound = null;
        
        // Try the my-enterprise endpoint first if user has enterpriseId
        if (user?.enterpriseId) {
          try {
            console.log('EnterpriseLogoDisplay: Fetching from /enterprise/my-enterprise');
            const response = await api.get('/enterprise/my-enterprise');
            console.log('EnterpriseLogoDisplay: my-enterprise response:', response);
            
            if (response?.success && response?.enterprise) {
              enterpriseFound = response.enterprise;
              console.log('EnterpriseLogoDisplay: Found enterprise via my-enterprise:', enterpriseFound.enterpriseName);
            }
          } catch (err) {
            console.warn('EnterpriseLogoDisplay: my-enterprise failed:', err.message);
          }
        }

        // If not found, try domain lookup
        if (!enterpriseFound && user.email.includes('@')) {
          const emailDomain = user.email.split('@')[1];
          try {
            console.log('EnterpriseLogoDisplay: Trying domain lookup for:', emailDomain);
            const response = await api.get(`/enterprise/check-domain/${emailDomain}`);
            console.log('EnterpriseLogoDisplay: check-domain response:', response);
            
            if (response?.exists && response?.enterprise) {
              enterpriseFound = response.enterprise;
              console.log('EnterpriseLogoDisplay: Found enterprise via domain:', enterpriseFound.enterpriseName);
            }
          } catch (err) {
            console.warn('EnterpriseLogoDisplay: Domain lookup failed:', err.message);
          }
        }

        if (enterpriseFound) {
          setEnterpriseData(enterpriseFound);
          setError(false);
          console.log('EnterpriseLogoDisplay: Successfully set enterprise data:', {
            name: enterpriseFound.enterpriseName,
            hasLogo: !!enterpriseFound.enterpriseImage,
            logoUrl: enterpriseFound.enterpriseImage
          });
        } else {
          console.log('EnterpriseLogoDisplay: No enterprise data found');
          setError(true);
        }
      } catch (err) {
        console.error('EnterpriseLogoDisplay: General error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have a user
    if (user) {
      fetchEnterpriseData();
    } else {
      setLoading(false);
      setError(true);
    }
  }, [user?.uid, user?.email, user?.enterpriseId, user?.enterpriseData]);

  if (loading) {
    return <EnterpriseLogoSkeleton isOpen={isOpen} />;
  }

  // Show actual enterprise data if we have it
  if (enterpriseData && !error) {
    // Use enterpriseImageUrl (fresh signed URL) with fallback to enterpriseImage for backwards compatibility
    const logoUrl = enterpriseData.enterpriseImageUrl || enterpriseData.enterpriseImage || enterpriseData.enterpriseLogo;
    
    if (isOpen) {
      return (
        <div className="flex items-center p-3 text-gray-700 rounded-lg dark:text-gray-400">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg overflow-hidden bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={`${enterpriseData.enterpriseName} logo`}
                className="h-full w-full object-contain"
                onError={(e) => {
                  console.log('EnterpriseLogoDisplay: Logo failed to load, showing fallback');
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`flex h-full w-full items-center justify-center ${logoUrl ? 'hidden' : 'flex'}`}
              style={{ display: logoUrl ? 'none' : 'flex' }}
            >
              <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-white block truncate">
              {enterpriseData.enterpriseName}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">Enterprise Account</p>
          </div>
        </div>
      );
    } else {
      // Collapsed sidebar - show only the logo
      return (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg overflow-hidden bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={`${enterpriseData.enterpriseName} logo`}
              className="h-full w-full object-contain"
              onError={(e) => {
                console.log('EnterpriseLogoDisplay: Logo failed to load (collapsed)');
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`flex h-full w-full items-center justify-center ${logoUrl ? 'hidden' : 'flex'}`}
            style={{ display: logoUrl ? 'none' : 'flex' }}
          >
            <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
        </div>
      );
    }
  }

  // Fallback UI when no enterprise data or error
  const fallbackName = user?.enterprise?.enterpriseName || 'Enterprise';
  
  if (isOpen) {
    return (
      <div className="flex items-center p-3 text-gray-700 rounded-lg dark:text-gray-400">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
          <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="ml-3 flex-1">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{fallbackName}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400">Enterprise Account</p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
        <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
      </div>
    );
  }
}

const navItems = [
  { 
    icon: <FileText className="w-5 h-5" />,
    name: 'Templates',
    path: '/app/'
  },
  { 
    icon: <Globe className="w-5 h-5" />,
    name: 'Websites',
    path: '/app/websites'
  },
  // Removed Custom Template menu
  {
    icon: <Lightbulb className="w-5 h-5" />,
    name: 'Copy Analyzer',
    path: '/app/copyAnalyzer'
  },
  { 
    icon: <Users className="w-5 h-5" />,
    name: 'Done For You',
    path: '/app/doneForYou'
  },
  {
    icon: <MessageSquareText className="w-5 h-5" />,
    name: 'Send a Text',
    path: '/app/sendText'
  },
  {
    divider: true,
    name: 'Video Ads',
    description: 'Browse and customize professional video ad templates designed for social media platforms. Create campaigns to collect authentic video testimonials from your community, then easily use these real community videos with our templates to create compelling social media ads.\n\nAccess our extensive library of professional stock videos to use in your campaign video advertisements and social media content.'
  },
  { 
    icon: <FileText className="w-5 h-5" />,
    name: 'Video Templates',
    path: '/app/videoTemplates'
  },
  { 
    icon: <Video className="w-5 h-5" />,
    name: 'Stock Videos',
    path: '/app/stockVideos'
  },
  {
    icon: <Users className="w-5 h-5" />,
    name: 'Supporter Videos',
    submenu: true,
    submenuItems: [
      { 
        icon: <Users className="w-5 h-5" />, 
        name: 'Create Campaign',
        path: '/app/campaigns/new'
      },
      {
        icon: <Users className="w-5 h-5" />,
        name: 'Manage Campaigns',
        path: '/app/campaigns'
      }
    ]
  }
];

const mobileNavItems = [
  { 
    icon: <FileText className="w-5 h-5" />,
    name: 'Templates',
    path: '/app/'
  },
  { 
    icon: <Globe className="w-5 h-5" />,
    name: 'Websites',
    path: '/app/websites'
  },
  // Removed Custom Template from mobile nav
  { 
    icon: <Lightbulb className="w-5 h-5" />,
    name: 'Copy Analyzer',
    path: '/app/copyAnalyzer'
  },
  { 
    icon: <Users className="w-5 h-5" />,
    name: 'Done For You',
    path: '/app/doneForYou'
  },
  {
    icon: <MessageSquareText className="w-5 h-5" />,
    name: 'Send a Text',
    path: '/app/sendText'
  },
  { 
    icon: <FileText className="w-5 h-5" />,
    name: 'Video Templates',
    path: '/app/videoTemplates'
  },
  { 
    icon: <Video className="w-5 h-5" />,
    name: 'Stock Videos',
    path: '/app/stockVideos'
  },
  { 
    icon: <Users className="w-5 h-5" />,
    name: 'Supporter Videos',
    path: '/app/campaigns'
  }
];

function Sidebar() {
  const isDark = useDarkMode();
  const location = useLocation();
  const { user } = useAuth();
  const { 
    isExpanded, 
    isMobileOpen, 
    isHovered, 
    setIsHovered, 
    toggleMobileSidebar,
    closeMobileSidebar 
  } = useSidebar();
  const { 
    openNamespaceModal, 
    isLoading,
    fetchUserNamespaces 
  } = useNamespace();

  const [openSubmenu, setOpenSubmenu] = useState('');

  // Check if user is enterprise - ONLY by database flag, NOT by email domain
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;

  // Toggle submenu open/closed
  const toggleSubmenu = (name) => {
    setOpenSubmenu(prev => prev === name ? '' : name);
  };

  const isOpen = isExpanded || isMobileOpen || isHovered;
  
  // Function to check if a navigation item is active based on the current path
  const isActiveRoute = (path) => {
    if (!path) return false;
    
    // Exact match for the root app path
    if (path === '/app/' && (location.pathname === '/app/' || location.pathname === '/app')) {
      return true;
    }
    
    // For video templates path
    if (path === '/app/videoTemplates') {
      return location.pathname === '/app/videoTemplates';
    }
    
    // For stock videos path
    if (path === '/app/stockVideos') {
      return location.pathname === '/app/stockVideos';
    }
    
    // For campaign paths, be more specific to avoid multiple highlights
    if (path === '/app/campaigns/new') {
      return location.pathname === '/app/campaigns/new';
    }
    
    if (path === '/app/campaigns') {
      return location.pathname === '/app/campaigns' || 
        (location.pathname.startsWith('/app/campaigns') && 
         location.pathname !== '/app/campaigns/new');
    }
    
    // For copy analyzer and generator paths
    if (path === '/app/copyAnalyzer') {
      return location.pathname === '/app/copyAnalyzer';
    }
    
    // Removed custom template submission route
    
    // For other paths, check if the current location starts with the path
    if (path !== '/app/') {
      return location.pathname.startsWith(path);
    }
    
    return false;
  };

  const handleItemClick = () => {
    if (isMobileOpen) {
      closeMobileSidebar();
    }
  };

  const handleNamespaceClick = () => {
    // Refresh the namespaces list before opening the modal
    fetchUserNamespaces(true);
    // Open the modal
    openNamespaceModal();
  };

  return (
    <aside
      className={`fixed lg:sticky top-0 flex flex-col px-5 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-[60] border-r border-gray-200 
        ${isOpen ? "w-[290px]" : "w-[90px]"}
        ${isMobileOpen ? "translate-x-0" : "lg:translate-x-0 -translate-x-full"}
        lg:translate-x-0 lg:relative lg:block`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col h-full">
        <div className={`py-8 flex items-center justify-center relative`}>
          <a href="/" className={`${isOpen ? 'w-full' : ''} flex justify-center`}>
            {/* Use a container with overflow hidden to create a smoother transition */}
            <div className="relative overflow-hidden transition-all duration-300 ease-in-out flex items-center justify-center"
                 style={{ 
                   width: isOpen ? '180px' : '32px', 
                   height: '48px'
                 }}>
              <img 
                src={`/images/logos/${isDark ? 'logo-big-white.png' : 'logo-big-colored.png'}`}
                alt="Advocacy Lab"
                className="h-12 w-auto object-contain transition-all duration-300 ease-in-out"
                style={{ 
                  transform: isOpen ? 'translateX(0)' : 'translateX(-36px)',
                  opacity: isOpen ? 1 : 0,
                  maxHeight: '48px'
                }}
              />
              <img 
                src={`/images/logos/${isDark ? 'logo-white.png' : 'logo-color.png'}`}
                alt="Advocacy Lab"
                className="h-7 w-auto object-contain absolute transition-all duration-300 ease-in-out"
                style={{ 
                  transform: isOpen ? 'translateX(100px)' : 'translateX(0)',
                  opacity: isOpen ? 0 : 1
                }}
              />
            </div>
          </a>
        </div>

        <nav className="flex-1 overflow-y-auto">
          <ul className="space-y-2 relative z-[70]">
            {navItems.map((item, index) => {
              // If this is a divider/header
              if (item.divider) {
                // For the first section, we don't need a top divider line
                const isFirstSection = index === 0;
                
                return (
                  <li key={`divider-${index}`} className={isFirstSection ? "pb-2 pt-1" : "py-4"}>
                    {isOpen && (
                      <>
                        {!isFirstSection && <div className="h-px bg-gray-200 dark:bg-gray-700 mb-3"></div>}
                        <div className="px-3 flex items-center">
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            {item.name}
                          </h3>
                          {item.description && (
                            <Tooltip text={item.description}>
                              <HelpCircle className="ml-1.5 w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                            </Tooltip>
                          )}
                        </div>
                      </>
                    )}
                    {!isOpen && !isFirstSection && (
                      <div className="h-px bg-gray-200 dark:bg-gray-700"></div>
                    )}
                  </li>
                );
              }
              
              // Check if the current item is active
              const isActive = isActiveRoute(item.path);
              
              // If this item has a submenu
              if (item.submenu) {
                // Check if any submenu item is active
                const isSubMenuActive = item.submenuItems.some(subItem => isActiveRoute(subItem.path));
                const isSubmenuOpen = openSubmenu === item.name;
                
                return (
                  <li key={item.name} className="relative">
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`flex items-center justify-between w-full p-3 rounded-lg 
                        ${isSubMenuActive && !isSubmenuOpen
                          ? 'text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
                    >
                      <div className="flex items-center">
                        {item.icon}
                        {isOpen && (
                          <div className="flex items-center">
                            <span className="ml-3">{item.name}</span>
                            {item.name === 'Testimonials' && (
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-medium rounded-full">New</span>
                            )}
                            {item.description && (
                              <Tooltip text={item.description}>
                                <HelpCircle className="ml-1.5 w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                      {isOpen && (
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </button>
                    
                    {/* Submenu items - only show if parent menu is open */}
                    {isSubmenuOpen && isOpen && (
                      <ul className="pl-4 mt-1 space-y-1">
                        {item.submenuItems.map(subItem => {
                          const isSubItemActive = isActiveRoute(subItem.path);
                          
                          return (
                            <li key={subItem.name}>
                              <Link
                                to={subItem.path}
                                onClick={handleItemClick}
                                className={`flex items-center p-3 rounded-lg 
                                  ${isSubItemActive
                                    ? `bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:text-white`
                                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
                                aria-current={isSubItemActive ? 'page' : undefined}
                              >
                                {subItem.icon}
                                <span className={`ml-3 ${isSubItemActive ? 'text-white' : ''}`}>
                                  {subItem.name}
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }
              
              // Regular menu item (no submenu)
              return (
                <li key={item.name} className="relative">
                  <Link
                    to={item.path}
                    onClick={handleItemClick}
                    className={`flex items-center p-3 rounded-lg 
                      ${isActive
                        ? `bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:text-white`
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.icon}
                    {isOpen && (
                      <div className="flex items-center">
                        <span className={`ml-3 ${isActive ? 'text-white' : ''}`}>
                          {item.name}
                        </span>
                        {item.badge && (
                          <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-medium rounded-full">
                            {item.badge}
                          </span>
                        )}
                        {item.description && (
                          <Tooltip text={item.description}>
                            <HelpCircle className="ml-1.5 w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
            
            {/* Mobile close button - only visible when mobile sidebar is open */}
            {isMobileOpen && (
              <>
                {/* Enterprise Logo or Namespace Selector for mobile - position above the close button */}
                <li className="lg:hidden mt-4">
                  {isEnterpriseUser ? (
                    <EnterpriseLogoDisplay isOpen={true} />
                  ) : (
                    <NamespaceSelector />
                  )}
                </li>
                <li className="lg:hidden mt-2">
                  <button
                    onClick={toggleMobileSidebar}
                    className="w-full flex items-center justify-center p-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors text-sm"
                  >
                    <X className="w-4 h-4 mr-2" />
                    <span>Close</span>
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>

        <div className="mt-auto pb-4 hidden lg:flex lg:flex-col">
          {/* Enterprise Logo or Namespace Selector for desktop - conditionally show based on user type */}
          {isEnterpriseUser ? (
            <EnterpriseLogoDisplay isOpen={isOpen} />
          ) : (
            <>
              {isOpen ? (
                <NamespaceSelector />
              ) : (
                <button
                  onClick={handleNamespaceClick}
                  className="flex items-center justify-center p-3 text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 text-purple-600 dark:text-purple-400 animate-spin" />
                    ) : (
                      <Building className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;